// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/TestimonialsSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiUpload, FiX } from 'react-icons/fi';
import api from '../../../api';
import ImagePreview from '../common/ImagePreview';

const TestimonialsSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  // 1. Keep track of global settings locally to prevent re-render parent cascades on every key stroke
  const [localTitle, setLocalTitle] = useState(websiteData?.testimonials?.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(websiteData?.testimonials?.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(websiteData?.testimonials?.displayStyle || 'featured-grid');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showRatings: websiteData?.testimonials?.showRatings !== false,
    showBatch: websiteData?.testimonials?.showBatch !== false,
    showCompany: websiteData?.testimonials?.showCompany !== false,
    showLocation: websiteData?.testimonials?.showLocation !== false,
  });

  const [localItems, setLocalItems] = useState(websiteData?.testimonials?.items || []);
  const [newItem, setNewItem] = useState({
    studentName: '',
    program: '',
    batch: '',
    currentPosition: '',
    company: '',
    location: '',
    testimonial: '',
    rating: 5
  });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync from parent props on clean initial mounts or hard reset updates from server
  useEffect(() => {
    console.log('[TestimonialsSection] Initializing and resetting all isolated states from source props data.');
    const section = websiteData?.testimonials || {};
    setLocalTitle(section.sectionTitle || '');
    setLocalSubtitle(section.sectionSubtitle || '');
    setLocalDisplayStyle(section.displayStyle || 'featured-grid');
    setLocalCheckboxes({
      showRatings: section.showRatings !== false,
      showBatch: section.showBatch !== false,
      showCompany: section.showCompany !== false,
      showLocation: section.showLocation !== false,
    });
    setLocalItems(section.items || []);
  }, [websiteData?.testimonials, tenantId]); 

  const handleToggle = () => {
    handleInputChange('testimonials', 'isActive', !websiteData?.testimonials?.isActive);
  };

  // Local Global Config Updators
  const handleGlobalConfigChange = (field, value) => {
    console.log(`[TestimonialsSection] Updating global config locally: Field [${field}] -> Value [${value}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => {
      const nextState = { ...prev, [key]: !prev[key] };
      console.log(`[TestimonialsSection] Toggling configuration option locally [${key}]:`, nextState[key]);
      return nextState;
    });
  };

  const handleAddItem = () => {
    if (!newItem.studentName || !newItem.testimonial) return;
    
    const item = {
      _id: Date.now().toString(),
      ...newItem,
      rating: Number(newItem.rating)
    };
    
    const updatedItems = [...localItems, item];
    console.log('[TestimonialsSection] Adding item to local list:', item);
    setLocalItems(updatedItems);
    
    // Sync structural adjustments up instantly
    handleInputChange('testimonials', 'items', updatedItems);
    
    setNewItem({
      studentName: '',
      program: '',
      batch: '',
      currentPosition: '',
      company: '',
      location: '',
      testimonial: '',
      rating: 5
    });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[TestimonialsSection] Deleting item row index location:', index);
    setLocalItems(updatedItems);
    
    // Sync structural adjustments up instantly
    handleInputChange('testimonials', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: field === 'rating' ? Number(value) : value };
    
    console.log(`[TestimonialsSection] Modifying item text locally -> Row [${index}] -> Field [${field}] -> Value [${value}]`);
    setLocalItems(updatedItems);
  };

  const handleImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      alert('Image size must be less than 1MB');
      event.target.value = '';
      return;
    }

    const localBlobPreview = URL.createObjectURL(file);
    const previewItems = [...localItems];
    previewItems[index] = { ...previewItems[index], studentImage: localBlobPreview };
    setLocalItems(previewItems);

    setUploading(prev => ({ ...prev, [index]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'testimonials');
      formData.append('itemIndex', index);
      formData.append('subField', 'studentImage');

      const response = await api.post(`/api/website/${tenantId}/upload/testimonials`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        console.log('[TestimonialsSection] Server host file uploaded:', response.data.data.url);
        const updatedItems = [...localItems];
        updatedItems[index] = { ...updatedItems[index], studentImage: response.data.data.url };
        setLocalItems(updatedItems);
        handleInputChange('testimonials', 'items', updatedItems);
      } else {
        alert(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(prev => ({ ...prev, [index]: false }));
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], studentImage: '' };
    setLocalItems(updatedItems);
    handleInputChange('testimonials', 'items', updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[TestimonialsSection] Shipping completely updated unified context parameters up to parent layout context state.');
    
    try {
      // Pushing everything to parent component immediately before issuing API request
      await handleInputChange('testimonials', 'sectionTitle', localTitle);
      await handleInputChange('testimonials', 'sectionSubtitle', localSubtitle);
      await handleInputChange('testimonials', 'displayStyle', localDisplayStyle);
      await handleInputChange('testimonials', 'showRatings', localCheckboxes.showRatings);
      await handleInputChange('testimonials', 'showBatch', localCheckboxes.showBatch);
      await handleInputChange('testimonials', 'showCompany', localCheckboxes.showCompany);
      await handleInputChange('testimonials', 'showLocation', localCheckboxes.showLocation);
      await handleInputChange('testimonials', 'items', localItems);

      const assembledPayload = {
        ...websiteData?.testimonials,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showRatings: localCheckboxes.showRatings,
        showBatch: localCheckboxes.showBatch,
        showCompany: localCheckboxes.showCompany,
        showLocation: localCheckboxes.showLocation,
        items: localItems
      };

      await handleSave('testimonials', assembledPayload);
    } finally {
      setSaving(false);
    }
  };

  const renderStars = (rating) => {
    return '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Row Action Section */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Student Testimonials</h2>
            <p className="text-sm text-slate-500 mt-0.5">Showcase student success stories and feedback</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              onClick={handleToggle}
              type="button"
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                websiteData?.testimonials?.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {websiteData?.testimonials?.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              onClick={handleSaveClick}
              disabled={saving}
              type="button"
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Isolated Section Config Fields */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Settings</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalConfigChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Student Voices"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalConfigChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Real stories from our students"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Style</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalConfigChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="featured-grid">Featured Grid</option>
                <option value="carousel">Carousel Slide</option>
                <option value="grid">Normal Grid</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Ratings', key: 'showRatings', checked: localCheckboxes.showRatings },
                { label: 'Show Batch', key: 'showBatch', checked: localCheckboxes.showBatch },
                { label: 'Show Company', key: 'showCompany', checked: localCheckboxes.showCompany },
                { label: 'Show Location', key: 'showLocation', checked: localCheckboxes.showLocation },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Existing Testimonial List Loop Block */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Added Testimonials</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Total
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {localItems.map((item, index) => (
              <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                <div className="p-4 sm:p-5 space-y-4">
                  
                  {/* Image Selector */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-slate-50 p-3 rounded-xl border border-slate-150">
                    <div className="flex-shrink-0 mx-auto sm:mx-0">
                      <div className="w-16 h-16 rounded-full border border-slate-200 flex items-center justify-center overflow-hidden bg-white shadow-inner relative group">
                        {item.studentImage ? (
                          <div className="w-full h-full relative">
                            <ImagePreview 
                              src={item.studentImage} 
                              alt={item.studentName || "Student Photo"} 
                              className="w-full h-full object-cover"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index)}
                              className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-full"
                            >
                              <FiX className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="p-2 text-slate-300">
                            <FiUpload className="w-5 h-5 mx-auto" />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-center sm:text-left flex-1">
                      <input
                        type="file"
                        id={`testimonial-image-${index}`}
                        accept="image/jpeg,image/png"
                        onChange={(e) => handleImageUpload(e, index)}
                        disabled={uploading[index]}
                        className="hidden"
                      />
                      <label
                        htmlFor={`testimonial-image-${index}`}
                        className={`inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 shadow-sm cursor-pointer transition-all ${
                          uploading[index] ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                        }`}
                      >
                        {uploading[index] ? 'Uploading...' : (item.studentImage ? 'Change Photo' : 'Upload Photo')}
                      </label>
                      <p className="text-[10px] text-slate-400 mt-1">Accepts JPG, PNG under 1MB</p>
                    </div>
                  </div>

                  {/* Input Form Fields Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Student Name</span>
                      <input
                        type="text"
                        value={item.studentName || ''}
                        onChange={(e) => handleItemChange(index, 'studentName', e.target.value)}
                        placeholder="Full Name"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 bg-slate-50/30 focus:bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Program</span>
                      <input
                        type="text"
                        value={item.program || ''}
                        onChange={(e) => handleItemChange(index, 'program', e.target.value)}
                        placeholder="Degree / Course"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 bg-slate-50/30 focus:bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Batch Year</span>
                      <input
                        type="text"
                        value={item.batch || ''}
                        onChange={(e) => handleItemChange(index, 'batch', e.target.value)}
                        placeholder="e.g., 2024"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 bg-slate-50/30 focus:bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Current Job Role</span>
                      <input
                        type="text"
                        value={item.currentPosition || ''}
                        onChange={(e) => handleItemChange(index, 'currentPosition', e.target.value)}
                        placeholder="e.g., Software Engineer"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 bg-slate-50/30 focus:bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Company Name</span>
                      <input
                        type="text"
                        value={item.company || ''}
                        onChange={(e) => handleItemChange(index, 'company', e.target.value)}
                        placeholder="e.g., Google"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 bg-slate-50/30 focus:bg-white"
                      />
                    </div>
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold uppercase text-slate-400">Location</span>
                      <input
                        type="text"
                        value={item.location || ''}
                        onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                        placeholder="e.g., New York, USA"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 bg-slate-50/30 focus:bg-white"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-400">Testimonial Description</span>
                    <textarea
                      value={item.testimonial || ''}
                      onChange={(e) => handleItemChange(index, 'testimonial', e.target.value)}
                      placeholder="Type the message details here..."
                      rows="3"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 bg-slate-50/30 focus:bg-white resize-y"
                    />
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-3 border-t border-slate-100">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2 bg-slate-100 px-2.5 py-1 rounded-lg">
                        <span className="text-xs font-semibold text-slate-600">Rating:</span>
                        <input
                          type="number"
                          value={item.rating || 5}
                          onChange={(e) => handleItemChange(index, 'rating', e.target.value)}
                          min="1"
                          max="5"
                          className="w-10 bg-transparent text-center font-bold text-slate-700 text-sm outline-none"
                        />
                      </div>
                      <div className="text-amber-400 tracking-wider text-sm select-none">
                        {renderStars(item.rating || 5)}
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={item.isFeatured || false}
                          onChange={(e) => handleItemChange(index, 'isFeatured', e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-4 h-4 rounded border transition-all flex items-center justify-center ${
                          item.isFeatured ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-xs bg-white ${item.isFeatured ? 'block' : 'hidden'}`} />
                        </div>
                        <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-800">Featured Highlight</span>
                      </label>

                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors"
                      >
                        <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Card
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Create Form Block */}
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <div className="p-1 bg-blue-50 rounded-md text-blue-600">
              <FiPlus className="h-4 w-4" />
            </div>
            <h4 className="text-sm font-bold text-slate-800">Create New Testimonial</h4>
          </div>
          
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                value={newItem.studentName}
                onChange={(e) => setNewItem({ ...newItem, studentName: e.target.value })}
                placeholder="Student Name *"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.program}
                onChange={(e) => setNewItem({ ...newItem, program: e.target.value })}
                placeholder="Program / Course Name"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.batch}
                onChange={(e) => setNewItem({ ...newItem, batch: e.target.value })}
                placeholder="Batch Year"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="text"
                value={newItem.currentPosition}
                onChange={(e) => setNewItem({ ...newItem, currentPosition: e.target.value })}
                placeholder="Current Job Position"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.company}
                onChange={(e) => setNewItem({ ...newItem, company: e.target.value })}
                placeholder="Company Name"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.location}
                onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                placeholder="Location"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
            </div>
            
            <textarea
              value={newItem.testimonial}
              onChange={(e) => setNewItem({ ...newItem, testimonial: e.target.value })}
              placeholder="Write the testimonial content here... *"
              rows="3"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500 resize-y"
            />
            
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <div className="flex items-center space-x-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 inline-flex self-start">
                <span className="text-xs font-semibold text-slate-500">Assign Rating:</span>
                <input
                  type="number"
                  value={newItem.rating}
                  onChange={(e) => setNewItem({ ...newItem, rating: Number(e.target.value) })}
                  min="1"
                  max="5"
                  className="w-8 bg-transparent text-center font-bold text-slate-700 text-sm outline-none"
                />
              </div>
              
              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center justify-center px-5 py-2 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm"
              >
                <FiPlus className="mr-1.5 h-4 w-4" />
                Insert Card
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default TestimonialsSection;